import {useBreakpointValue} from '@chakra-ui/react'

import {
    buildResponsive,
    hasValue,
    mapResponsiveValue,
    parseResponsiveViewTypes
} from '../../utils/gdi-responsive'
import {
    fillGDIResponsiveInheritance,
    hasGDIViewTypes,
    parseGDIViewTypes
} from '../../utils/gdi-styles'
import {getAllExpandedFromContentArea} from '../../utils/gdi-content-area'

import {JUSTIFY_MAP, toInt} from './helpers'
import {buildArrowSx} from './arrows'

const ALL_WEB_BREAKPOINTS = ['base', 'md', 'lg']

/* Resolves every authored field on `GDICarouselGridBlockStylesPerViewType`
 * into the responsive/scalar values the component needs, including the
 * carousel-level toggles (Draggable, Autoplay, InfiniteMode, etc.) — these
 * can be per-breakpoint or global depending on the field.
 * Returns a single config object so the main
 * component stays declarative. */
export const useCarouselGridConfig = (stylesArr) => {
    const defaultEntry =
        (Array.isArray(stylesArr) &&
            stylesArr.find((entry) => !entry?.ViewTypesToApply?.trim?.())) ||
        {}

    /* DisplayType is per-breakpoint — author can mix Grid (mobile) with
     * Carousel (desktop) on the same block. */
    const displayTypeOf = (e) => {
        if (!e || !hasValue(e.DisplayType)) return null
        return e.DisplayType === 'Carousel' ? 'Carousel' : 'Grid'
    }
    /* If the block is itself a tile in a parent grid (any per-view-type
     * entry sets `ColSpan`), the author placed it to occupy a column. In
     * that case we must NOT enter "intrinsic width" mode when TilesPerRow
     * is unauthored — otherwise the inner tiles shrink-wrap to their
     * content and authored alignment (e.g. ContentHorizontalPosition:
     * Right) has no horizontal slack to act on. We treat unauthored
     * TilesPerRow as 1 so the inner tile fills the parent column. */
    const isNestedInGrid =
        Array.isArray(stylesArr) &&
        stylesArr.some((e) => Number.isFinite(Number(e?.ColSpan)) && Number(e?.ColSpan) > 0)
    const tilesPerRowAuto =
        !isNestedInGrid &&
        (!Array.isArray(stylesArr) ||
            stylesArr.every((e) => !hasValue(e?.TilesPerRow) || Number(e?.TilesPerRow) === 0))
    const hasPartialTilePeek =
        tilesPerRowAuto ||
        (Array.isArray(stylesArr) &&
            stylesArr.some((e) => {
                if (!hasValue(e?.TilesPerRow)) return false
                const value = Number(e.TilesPerRow)
                return Number.isFinite(value) && !Number.isInteger(value)
            }))

    /* When TilesPerRow is unauthored at every breakpoint, tiles size to their
     * intrinsic content width; the row scrolls horizontally when the combined
     * width exceeds the viewport. We force Carousel mode so `flex-wrap: nowrap`
     * + `overflow-x: auto` produce that behavior — UNLESS the author explicitly
     * set DisplayType = 'Grid' on at least one entry, in which case we respect
     * the authored intent. `tilesPerRow` is left at the standard fallback so
     * other consumers (e.g. GDIGridColumnsContext) keep working sensibly. */
    const tilesPerRow = buildResponsive(
        stylesArr,
        (e) => (hasValue(e.TilesPerRow) ? Number(e.TilesPerRow) : null),
        /* Nested-in-grid blocks default to a single tile per row so the
         * inner tile fills the parent column; standalone blocks keep the
         * original 1/2/3 responsive ramp. */
        isNestedInGrid ? 1 : {base: 1, md: 2, lg: 3}
    )
    /* Per-bp authored DisplayType (null = unauthored anywhere). Considers both
     * default and view-type-targeted entries — when the author explicitly picks
     * Grid in the CMS dropdown (default entry counts), we honour that even
     * with TilesPerRow=0 so tiles render at their intrinsic width and the row
     * wraps to a new line (flex-wrap: wrap) instead of becoming a horizontal
     * carousel. The Carousel intrinsic-width shortcut still applies when
     * DisplayType is unauthored or explicitly Carousel. */
    const authoredDisplayTypeByBp = buildResponsive(stylesArr, displayTypeOf, null)
    const isAuthoredGridAt = (bp) => {
        if (authoredDisplayTypeByBp === null) return false
        if (typeof authoredDisplayTypeByBp !== 'object')
            return authoredDisplayTypeByBp === 'Grid'
        return authoredDisplayTypeByBp[bp] === 'Grid'
    }
    const hasAuthoredGrid =
        authoredDisplayTypeByBp === 'Grid' ||
        (typeof authoredDisplayTypeByBp === 'object' &&
            authoredDisplayTypeByBp !== null &&
            Object.values(authoredDisplayTypeByBp).some((v) => v === 'Grid'))
    /* When a specific breakpoint has TilesPerRow=0, treat it as per-breakpoint
     * auto mode — force Carousel (flex-wrap: nowrap) there so tiles flow at
     * their intrinsic width alongside tiles with explicit counts on other bps.
     * Exception: if the author explicitly chose Grid for that bp, keep Grid
     * so tiles wrap to a new line instead of scrolling horizontally. */
    const baseDisplayTypeByBp =
        tilesPerRowAuto && !hasAuthoredGrid
            ? 'Carousel'
            : buildResponsive(stylesArr, displayTypeOf, 'Grid')
    const displayTypeByBp = (() => {
        if (tilesPerRowAuto && !hasAuthoredGrid) return baseDisplayTypeByBp
        const tprObj =
            typeof tilesPerRow === 'object' && tilesPerRow !== null
                ? tilesPerRow
                : {base: tilesPerRow}
        if (!Object.values(tprObj).some((v) => v === 0)) return baseDisplayTypeByBp
        const dtObj =
            typeof baseDisplayTypeByBp === 'object' && baseDisplayTypeByBp !== null
                ? {...baseDisplayTypeByBp}
                : {
                      base: baseDisplayTypeByBp || 'Grid',
                      md: baseDisplayTypeByBp || 'Grid',
                      lg: baseDisplayTypeByBp || 'Grid'
                  }
        for (const [bp, v] of Object.entries(tprObj)) {
            if (v === 0 && !isAuthoredGridAt(bp)) dtObj[bp] = 'Carousel'
        }
        return dtObj
    })()
    /* On the server `useBreakpointValue` returns the `base` value; the client
     * re-resolves after hydration. Used only for things that can't be
     * expressed in CSS alone (clones, arrow render gating). */
    const currentDisplayType =
        useBreakpointValue(
            typeof displayTypeByBp === 'object' && displayTypeByBp
                ? displayTypeByBp
                : {base: displayTypeByBp || 'Grid'}
        ) || 'Grid'
    const tilesGap = buildResponsive(
        stylesArr,
        (e) => (hasValue(e.TilesGap) ? `${e.TilesGap}px` : null),
        '0px'
    )
    const justify = buildResponsive(
        stylesArr,
        (e) => JUSTIFY_MAP[e.ContentJustification],
        'flex-start'
    )
    /* Per-slide alignment for CTA-only content. The CTA button bakes in
     * `alignSelf: 'flex-start'` so it doesn't get stretched by the slide
     * wrapper — but that means the slide-level `justify-content` (which
     * only kicks in when there's slack along the row main axis) can't
     * recenter it. We expose a parallel `align-self` value mapped from
     * ContentJustification so the slide wrapper can re-align CTA children
     * directly. Only meaningful for Left/Center/Right; the
     * Space-between/Space-around values don't translate to alignSelf. */
    const ALIGN_SELF_MAP = {
        Left: 'flex-start',
        Center: 'center',
        Right: 'flex-end'
    }
    const ctaAlignSelf = buildResponsive(
        stylesArr,
        (e) => ALIGN_SELF_MAP[e.ContentJustification],
        undefined
    )

    /* Arrows visibility — desktop default + optional hover override.
     * Default (no override anywhere): hidden in steady state, visible on hover.
     * The fallback chain (hover ← desktop ← primary) still applies, so an
     * author who sets `Arrows: Yes` on the primary entry gets always-visible
     * arrows; an author who sets `Arrows: No` gets always-hidden arrows. */
    const findEntry = (...viewTypes) =>
        Array.isArray(stylesArr)
            ? stylesArr.find((entry) => {
                  const targets = parseGDIViewTypes(entry?.ViewTypesToApply)
                  return viewTypes.some((viewType) => targets.includes(viewType))
              })
            : null
    const desktopEntry = findEntry('lg')
    const desktopHoverEntry = findEntry('_hover')
    const resolveAuthored = (raw) => (hasValue(raw) ? raw : null)
    const desktopArrowsAuthored = resolveAuthored(
        hasValue(desktopEntry?.Arrows) ? desktopEntry.Arrows : defaultEntry.Arrows
    )
    const hoverArrowsAuthored = resolveAuthored(
        hasValue(desktopHoverEntry?.Arrows) ? desktopHoverEntry.Arrows : desktopArrowsAuthored
    )
    /* Default-hidden steady state: only `Yes` shows. Default-visible hover
     * state: only `No` hides; `null` (unauthored) keeps arrows visible. */
    const showOnDesktop = desktopArrowsAuthored === 'Yes'
    const showOnHover = hoverArrowsAuthored === null ? true : hoverArrowsAuthored === 'Yes'

    /* Per-breakpoint Arrows authoring (mobile/tablet). Touch devices have no
     * hover, so we only consider the steady-state `Yes`/`No` here. `null`
     * (unauthored) → arrows hidden on that breakpoint, preserving the
     * historical mobile/tablet behaviour. */
    const arrowsAuthoredByBp = buildResponsive(
        stylesArr,
        (e) => (hasValue(e?.Arrows) ? e.Arrows : null),
        null
    )
    const arrowsAuthoredAt = (bp) => {
        if (arrowsAuthoredByBp === null) return null
        if (typeof arrowsAuthoredByBp === 'object') return arrowsAuthoredByBp[bp] ?? null
        return arrowsAuthoredByBp
    }
    const arrowsVisibleByBp = {
        base: arrowsAuthoredAt('base') === 'Yes',
        md: arrowsAuthoredAt('md') === 'Yes',
        lg: showOnDesktop
    }
    /* Always render the arrow node at lg so the desktop hover-reveal sx can
     * fade it in; only mount mobile/tablet when explicitly authored. */
    const arrowDisplay = {
        base: arrowsVisibleByBp.base ? 'flex' : 'none',
        md: arrowsVisibleByBp.md ? 'flex' : 'none',
        lg: 'flex'
    }
    const showArrows =
        arrowsVisibleByBp.base || arrowsVisibleByBp.md || arrowsVisibleByBp.lg || showOnHover
    const arrowSx = buildArrowSx(arrowsVisibleByBp, showOnHover)

    const buildResponsiveYesNoFlag = (key, fallback) => {
        const resolved = buildResponsive(
            stylesArr,
            (entry) => (hasValue(entry?.[key]) ? entry[key] !== 'No' : null),
            fallback
        )

        if (resolved && typeof resolved === 'object') return resolved
        return Object.fromEntries(ALL_WEB_BREAKPOINTS.map((bp) => [bp, resolved]))
    }

    const draggableByBp = buildResponsiveYesNoFlag('Draggable', true)
    const currentDraggable = useBreakpointValue(draggableByBp)
    const draggable = currentDraggable !== false

    /* Remaining carousel-level toggles are global. Take first entry with a
     * value so authors can set the flag on any single breakpoint entry. */
    const firstSetValue = (key) => {
        if (hasValue(defaultEntry?.[key])) return defaultEntry[key]
        if (!Array.isArray(stylesArr)) return undefined
        for (const e of stylesArr) {
            if (hasValue(e?.[key])) return e[key]
        }
        return undefined
    }
    const autoplay = firstSetValue('GDICarouselGridBlockAutoplay') === 'Yes'
    const loop = firstSetValue('GDICarouselGridBlockInfiniteMode') === 'Yes'
    const delay = toInt(firstSetValue('GDICarouselGridBlockTimeBetweenSlides'), 4000)
    const tilesToScroll = toInt(firstSetValue('TilesToScroll'), 1)

    /* Section-level wrapper styles, authored flat on each per-view-type entry. */

    /* Maps special SectionWidth CMS tokens to concrete CSS max-width values.
     * 6-cols-desktop ≈ 692px; 8-cols-desktop ≈ 924px. Add new entries here to
     * support additional column-width presets. */
    const SECTION_WIDTH_TOKEN_MAP = {
        'max-width-6-cols-desktop': '692px',
        'max-width-8-cols-desktop': '924px'
    }
    const DESKTOP_SECTION_WIDTH_TOKENS = new Set(Object.keys(SECTION_WIDTH_TOKEN_MAP))
    const getSectionWidthBreakpoints = (entry, value) => {
        if (DESKTOP_SECTION_WIDTH_TOKENS.has(value)) return ['lg']
        if (!hasGDIViewTypes(entry)) return ['base', 'md', 'lg']
        return parseResponsiveViewTypes(entry?.ViewTypesToApply)
    }
    const buildSectionWidthResponsive = () => {
        if (!Array.isArray(stylesArr) || stylesArr.length === 0) return null
        const out = {}
        const applyEntry = (entry) => {
            const value =
                typeof entry?.SectionWidth === 'string'
                    ? entry.SectionWidth.trim()
                    : entry?.SectionWidth
            if (!hasValue(value)) return
            getSectionWidthBreakpoints(entry, value).forEach((bp) => {
                out[bp] = value
            })
        }

        stylesArr.filter((entry) => !hasGDIViewTypes(entry)).forEach(applyEntry)
        stylesArr.filter((entry) => hasGDIViewTypes(entry)).forEach(applyEntry)

        return Object.keys(out).length ? fillGDIResponsiveInheritance(out) : null
    }

    /* Keep raw values so fluid/full-bleed flags can inspect the original tokens,
     * then resolve col-based tokens to real CSS before returning to the component. */
    const rawSectionWidth = buildSectionWidthResponsive()
    const sectionWidth = rawSectionWidth
        ? mapResponsiveValue(rawSectionWidth, (v) => SECTION_WIDTH_TOKEN_MAP[v] ?? v)
        : rawSectionWidth

    const sectionColSpan = buildResponsive(
        stylesArr,
        (e) => {
            const v = e?.ColSpan
            return Number.isFinite(Number(v)) && Number(v) > 0 ? Number(v) : null
        },
        null
    )
    const sectionBackgroundColor = buildResponsive(stylesArr, (e) => e?.BackgroundColor, null)

    const sectionMargin = buildResponsive(
        stylesArr,
        (e) => e?.GDICarouselGridBlockMarginPerDevice,
        undefined
    )

    const sectionPadding = buildResponsive(
        stylesArr,
        (e) => e?.GDICarouselGridBlockPaddingPerDevice,
        undefined
    )

    /* BackgroundMedia is a content area; CSS can't responsively swap rendered
     * components, so we take the first authored media. */
    const mediaArr = (Array.isArray(stylesArr) ? stylesArr : [])
        .map((e) => e?.GDICarouselGridBlockBackgroundMedia)
        .find((arr) => Array.isArray(arr) && arr.length > 0)
    const sectionBackgroundMedia = mediaArr
        ? getAllExpandedFromContentArea(mediaArr)[0] || null
        : null

    /* Title is a content area authored INSIDE each per-view-type style entry,
     * so it varies by breakpoint. Resolve to one entry per Chakra breakpoint
     * (`base` / `md` / `lg`) using mobile-first inheritance: a breakpoint
     * with no authored title inherits the closest smaller breakpoint that
     * does. We then build a flat union of all blocks (for stable React keys)
     * and tag each block with the per-breakpoint `display` map it should
     * render with — `block` on the breakpoints whose entry includes it,
     * `none` on the rest. The renderer wraps each block in that display
     * map so a block authored only for `lg` doesn't appear on `base`/`md`.
     * A default entry (no ViewTypesToApply) acts as the universal fallback. */
    const titleByBp = {base: null, md: null, lg: null}
    ;(Array.isArray(stylesArr) ? stylesArr : []).forEach((entry) => {
        const blocks = getAllExpandedFromContentArea(entry?.GDICarouselGridBlockTitle)
        if (!blocks.length) return
        const targets = parseGDIViewTypes(entry?.ViewTypesToApply).filter((bp) =>
            ALL_WEB_BREAKPOINTS.includes(bp)
        )
        const bps = targets.length ? targets : ALL_WEB_BREAKPOINTS
        bps.forEach((bp) => {
            titleByBp[bp] = blocks
        })
    })
    /* Mobile-first inheritance for unauthored breakpoints. */
    if (titleByBp.md === null) titleByBp.md = titleByBp.base
    if (titleByBp.lg === null) titleByBp.lg = titleByBp.md

    const titleOrder = []
    const titleBlockByKey = new Map()
    const titleVisByKey = new Map()
    let titleAnonCounter = 0
    ALL_WEB_BREAKPOINTS.forEach((bp) => {
        ;(titleByBp[bp] || []).forEach((block) => {
            const key =
                block?.ContentLink?.GuidValue ||
                (Number.isFinite(Number(block?.ContentLink?.Id))
                    ? `id-${block.ContentLink.Id}`
                    : `anon-${titleAnonCounter++}`)
            if (!titleBlockByKey.has(key)) {
                titleBlockByKey.set(key, block)
                titleVisByKey.set(key, {base: false, md: false, lg: false})
                titleOrder.push(key)
            }
            titleVisByKey.get(key)[bp] = true
        })
    })

    const sectionTitle = titleOrder.map((key) => {
        const vis = titleVisByKey.get(key)
        return {
            key,
            block: titleBlockByKey.get(key),
            displayByBp: {
                base: vis.base ? 'block' : 'none',
                md: vis.md ? 'block' : 'none',
                lg: vis.lg ? 'block' : 'none'
            }
        }
    })
    const sectionGridColumn = mapResponsiveValue(sectionColSpan, (n) => `span ${n}`)

    /* SectionWidth modes:
     *   "fluid-container"         → full viewport width with inner gutter padding.
     *   "full-bleed"              → full viewport width with NO inner padding.
     *   "max-width-6-cols-desktop"→ constrained to 692px (6 of 12 cols) at lg
     *                               only, regardless of the authored view type;
     *                               centered via mx:auto.
     *   "max-width-8-cols-desktop"→ constrained to 924px (8 of 12 cols);
     *                               at lg only, regardless of the authored view type;
     *                               centered via mx:auto.
     * Both fluid flags imply `isFluid`; `isFullBleed` only removes inner padding.
     * Col-based tokens are resolved to CSS percentages in `sectionWidth` (above)
     * and treated as `hasMaxWidth = true` — no fluid behaviour is applied. */
    const rawWidthValues =
        typeof rawSectionWidth === 'object' && rawSectionWidth
            ? Object.values(rawSectionWidth)
            : [rawSectionWidth]
    const isFullBleed = rawWidthValues.includes('full-bleed')
    const isFluid = isFullBleed || rawWidthValues.includes('fluid-container')
    const hasMaxWidth = !isFluid && rawWidthValues.some(hasValue)

    return {
        displayTypeByBp,
        currentDisplayType,
        tilesPerRow,
        tilesPerRowAuto,
        hasPartialTilePeek,
        tilesGap,
        justify,
        ctaAlignSelf,
        showArrows,
        arrowSx,
        arrowDisplay,
        draggable,
        draggableByBp,
        autoplay,
        loop,
        delay,
        tilesToScroll,
        sectionWidth,
        sectionBackgroundColor,
        sectionBackgroundMedia,
        sectionTitle,
        sectionColSpan,
        sectionGridColumn,
        sectionMargin,
        sectionPadding,
        isFluid,
        isFullBleed,
        hasMaxWidth
    }
}
